import React from "react";
import TitleStripe from "../TitleStripe";

/**
 * MediaDiasInadimplenciaPJQuery (layout antigo + ponteiro correto + badge abaixo da seta)
 * Faixas fixas: OK ≤ 10, Atenção >10 e <20, Crítico ≥20
 */
export default function MediaDiasInadimplenciaPJQuery({
  data,
  maxDays = 50,
  loading = false,
  error = "",
  title = "Média de dias de inadimplência",
  className = "",
}) {
  const GOOD = 10;
  const WARN = 20;

  // === Sem dados suficientes ===
  if (data?.indicador == null) {
    return (
      <div>
        <TitleStripe>Média dias inadimplência</TitleStripe>
        <div
          className={[
            "rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:p-5 mt-4",
            className,
          ].join(" ")}
        >
          <h3 className="text-base font-semibold text-slate-900">{title}</h3>
          <p className="mt-0.5 text-xs text-slate-500">
            Quanto tempo, em média, esta empresa atrasa para realizar o
            pagamento para seus fornecedores.
          </p>

          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            Não foi possível calcular a média de dias de inadimplência:
            indicador indisponível.
          </div>
        </div>
      </div>
    );
  }

  // Normalização robusta
  const num = Number(data?.indicador);
  const value = Number.isFinite(num) ? Math.max(0, num) / 12 : null;

  // Classificação por DIAS
  const level =
    value == null
      ? "neutral"
      : value <= GOOD
      ? "good"
      : value < WARN
      ? "warn"
      : "bad";

  // % do range apenas para posicionar o ponteiro (clampado)
  const pct =
    value == null
      ? 0
      : Math.max(0, Math.min(100, (Math.min(value, maxDays) / maxDays) * 100));

  return (
    <div>
      <TitleStripe>Média dias inadimplência</TitleStripe>
      <div
        className={[
          "rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:p-5 mt-4",
          "flex flex-col gap-4",
          className,
        ].join(" ")}
      >
        {/* Cabeçalho */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-slate-900">{title}</h3>
            <p className="text-xs text-slate-500">
              Quanto tempo, em média, esta empresa atrasa para realizar o
              pagamento para seus fornecedores.
            </p>
          </div>
          <StatusBadge level={level} value={value} />
        </div>

        {/* Valor + Medidor */}
        <div className="flex flex-col gap-3">
          <div className="text-3xl font-bold leading-none text-slate-900">
            {loading ? (
              <span className="inline-block h-8 w-24 animate-pulse rounded-md bg-slate-200" />
            ) : value == null ? (
              "—"
            ) : (
              <>
                {Math.round(value)}
                <span className="ml-1 text-sm font-medium text-slate-500">
                  dias
                </span>
              </>
            )}
          </div>

          <PillMeterOld
            pct={pct}
            value={value}
            level={level}
            maxDays={maxDays}
            GOOD={GOOD}
            WARN={WARN}
            ariaLabel={
              value == null
                ? "Medidor sem valor"
                : `Inadimplência média de ${Math.round(
                    value
                  )} dias em uma escala de ${maxDays} dias`
            }
          />
        </div>

        {/* Rodapé */}
        {error ? (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </div>
        ) : (
          <Legend GOOD={GOOD} WARN={WARN} active={level} />
        )}
      </div>
    </div>
  );
}

/* ---------- Pill meter (layout antigo) + badge abaixo da seta ---------- */

function PillMeterOld({ pct, value, level, maxDays, GOOD, WARN, ariaLabel }) {
  // ticks específicos (0, 10, 20, maxDays)
  const ticks = [
    { posPct: 0, label: "0d", align: "text-left" },
    { posPct: (GOOD / maxDays) * 100, label: `${GOOD}d`, align: "text-center" },
    { posPct: (WARN / maxDays) * 100, label: `${WARN}d`, align: "text-center" },
    { posPct: 100, label: `${maxDays}d`, align: "text-right" },
  ];

  const leftStyle = { left: `${pct}%` };

  // estilo do badge conforme faixa
  const badgeCls =
    level === "good"
      ? "bg-emerald-100 text-emerald-700 border-emerald-200"
      : level === "warn"
      ? "bg-amber-100 text-amber-700 border-amber-200"
      : level === "bad"
      ? "bg-rose-100 text-rose-700 border-rose-200"
      : "bg-slate-100 text-slate-700 border-slate-200";

  const badgeDot =
    level === "good"
      ? "bg-emerald-500"
      : level === "warn"
      ? "bg-amber-500"
      : level === "bad"
      ? "bg-rose-500"
      : "bg-slate-400";

  return (
    <div className="w-full" role="img" aria-label={ariaLabel}>
      {/* Container relativo para posicionar seta + badge */}
      <div className="relative">
        {/* Barra com gradiente */}
        <div className="relative h-4 w-full rounded-full border border-slate-200 bg-gradient-to-r from-emerald-400 via-amber-300 to-rose-400">
          <div className="absolute inset-0 rounded-full bg-white/30 mix-blend-overlay" />

          {/* Ponteiro centralizado */}
          <div
            className="absolute top-1/2 -translate-y-1/2"
            style={leftStyle}
            aria-hidden
          >
            <div className="relative -ml-[6px] h-6 w-[12px]">
              <div className="absolute left-1/2 top-0 h-6 w-[8px] -translate-x-1/2 bg-slate-900/70 rounded-xl" />
            </div>
          </div>

          {/* Borda suave */}
          <div className="pointer-events-none absolute inset-0 rounded-full ring-1 ring-black/5" />
        </div>
      </div>
    </div>
  );
}

/* ---------- Badges das faixas (sincronizadas) ---------- */
function Legend({ GOOD, WARN, active }) {
  const chip = (isActive, base) =>
    [
      "inline-flex items-center gap-2 rounded-full border px-2 py-1 text-[10px]",
      isActive ? "ring-2 ring-offset-1" : "",
      base,
    ].join(" ");

  return (
    <div className="flex flex-wrap items-center gap-3 text-[11px]">
      <span
        className={chip(
          active === "good",
          "border-emerald-200 bg-emerald-50 text-emerald-700"
        )}
      >
        <span className="inline-block h-2.5 w-2.5 rounded-full bg-emerald-500" />
        OK (≤ 10d)
      </span>
      <span
        className={chip(
          active === "warn",
          "border-amber-200 bg-amber-50 text-amber-700"
        )}
      >
        <span className="inline-block h-2.5 w-2.5 rounded-full bg-amber-500" />
        Atenção (&gt; 10 e &lt; 20d)
      </span>
      <span
        className={chip(
          active === "bad",
          "border-rose-200 bg-rose-50 text-rose-700"
        )}
      >
        <span className="inline-block h-2.5 w-2.5 rounded-full bg-rose-500" />
        Crítico (≥ 20d)
      </span>
    </div>
  );
}

/* ---------- Badge de status do cabeçalho ---------- */
function StatusBadge({ level, value }) {
  const map = {
    neutral: {
      text: "Sem dado",
      cls: "bg-slate-100 text-slate-700 border-slate-200",
      dot: "bg-slate-400",
    },
    good: {
      text: "OK",
      cls: "bg-emerald-100 text-emerald-700 border-emerald-200",
      dot: "bg-emerald-500",
    },
    warn: {
      text: "Atenção",
      cls: "bg-amber-100 text-amber-700 border-amber-200",
      dot: "bg-amber-500",
    },
    bad: {
      text: "Crítico",
      cls: "bg-rose-100 text-rose-700 border-rose-200",
      dot: "bg-rose-500",
    },
  }[level];

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs ${map.cls}`}
    >
      <span className={`inline-block h-2 w-2 rounded-full ${map.dot}`} />
      {map.text}
      {typeof value === "number" && (
        <span className="ml-1 font-medium text-slate-700/70">
          ({Math.round(value)} d)
        </span>
      )}
    </span>
  );
}
